import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormNumericField } from "@/components/UiKit/FormControls/FormNumericField"
import {
  type DeliveryDateLinePriceOverrideFormValues,
  deliveryDateLinePriceOverrideFormSchema,
} from "@/screens/CustomerOrders/schemas"

interface SalesOrderLinePriceOverrideDialogProps {
  pricePerUom: number | null
  onClose: () => void
  onSubmit: (formValues: DeliveryDateLinePriceOverrideFormValues) => void
}

export const SalesOrderLinePriceOverrideDialog: FC<SalesOrderLinePriceOverrideDialogProps> = ({
  pricePerUom,
  onClose,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = useForm<DeliveryDateLinePriceOverrideFormValues>({
    defaultValues: { pricePerUom },
    resolver: zodResolver(deliveryDateLinePriceOverrideFormSchema),
    mode: "onChange",
  })

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  const handleFormSubmit = handleSubmit((formValues) => {
    onSubmit(formValues)
  })

  return (
    <Dialog open onClose={onClose} maxWidth='xs' fullWidth data-testid='customer-orders.so-line-price-override-dialog'>
      <DialogHeader title='Override Price' onClose={onClose} />
      <DialogContent>
        <FormNumericField
          control={control}
          name='pricePerUom'
          label='Price per UoM'
          required
          data-testid='customer-orders.so-line-price-override-dialog.price-field'
        />
      </DialogContent>
      <DialogActions className='flex justify-between px-6 pb-6'>
        <Button
          appearance='outlined'
          onClick={onClose}
          data-testid='customer-orders.so-line-price-override-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          onClick={handleFormSubmit}
          disabled={!canSubmit}
          data-testid='customer-orders.so-line-price-override-dialog.submit-button'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
